import { db } from "../firebase"; 
import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  getDocFromServer,
  addDoc,
  updateDoc,
  collection,
  collectionGroup,
  query,
  orderBy,
  limit,
  where,
  deleteDoc,
  writeBatch,
  serverTimestamp,
  getFirestore
} from "firebase/firestore";
import { getAuth } from "firebase/auth";



export const SHEET_HEADERS = [
  "Trade #",
  "Product",
  "Running Balance",
  "Price Per Tick",
  "Risk To Reward Ratio",
  "Percent of Balance",
  "Stop Loss_Take Profit",
  "Suggested Max # of Contracts",
  "Total Risk",
  "Max Stop Loss Size for 1 Contract(s) (Ticks)",
  "Total Target Reward",
  "Actual Percent Growth",
  "Actual Reward",
  "Potential Balance",
  "Win",
  "Loss",
  "Actual Balance",
  "Size",
  "Date",
  "Entry Time",
  "Exit Time",
  "Entry Price",
  "Exit Price",
  "Duration (h:mm)",
  "Actual Risk to Reward",
  "Session",
  "Entry Strategy",
  "Exit Strategy",
  "Did I follow my Rules?",
  "Ticks",
  "Notes",
  "Grade",
  "Mood_Mindset",
  "IdealSL",
  "Links_Screenshots",
  "Position",
  "Lesson"
];

export const defaultSettings = {
  Product: "",
  "Initial Balance": 2000,
  "Maximum Contracts": 5,
  "Stop Loss": 50,
  "Daily Goal": 500,
  "Minimum Contracts": 1,
  "Take Profit": 100,
  "Daily Loss Limit": 200,
  "Account Goal": 5000,
  "Running Balance": 2000,
  "Risk Percentage": 2,
  "Number of Trades to Goal": 0,
  "Max Trades Per Day": 10,
  "Max Loss Trades Per Day": 5,
  "Drawdown Limit %": 10,
  "Pretrade Checklist": "Yes",
  Theme: "Dark",
  "Trade Mode": "Futures",
  // ...any other settings
};

function fixNumericDefaults(obj) {
  const fixed = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (typeof v === "string" && v.trim() !== "" && !isNaN(Number(v))) {
      fixed[k] = Number(v);
    } else {
      fixed[k] = v;
    }
  });
  return fixed;
}

// FirestoreService.js
export async function getSettings(userId, accountId) {
  const docRef = doc(db, "users", userId, "accounts", accountId);
  const docSnap = await getDoc(docRef);
  let data = docSnap.data() || {};
  let settings = data.settings || {}; // <--- grab only the settings
  return { ...defaultSettings, ...settings };
}


// FirestoreService.js
export async function updateSettings(userId, accountId, settingsObj) {
  const docRef = doc(db, "users", userId, "accounts", accountId);
  await setDoc(docRef, { settings: settingsObj }, { merge: true }); // always nest under "settings"
}


export async function appendTrade(userId, accountId, tradeObj) {
  const colRef = collection(db, "users", userId, "accounts", accountId, "journal");
  const sanitizedTrade = removeUndefined(tradeObj);
  return await addDoc(colRef, { ...sanitizedTrade, created: serverTimestamp() });
}

// Fetch all trades for an account
export async function fetchJournalRows(userId, accountId) {
  const colRef = collection(db, "users", userId, "accounts", accountId, "journal");
  const q = query(colRef, orderBy("created"));
  const snap = await getDocs(q);
  return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

// Update a journal row
export async function updateJournalRow(userId, accountId, tradeId, updatedTrade) {
  const docRef = doc(db, "users", userId, "accounts", accountId, "journal", tradeId);
  await updateDoc(docRef, updatedTrade);
}

// Delete a journal row
export async function deleteJournalRow(userId, accountId, tradeId) {
  const docRef = doc(db, "users", userId, "accounts", accountId, "journal", tradeId);
  await deleteDoc(docRef);
}


// Get all trading accounts for a user
export async function fetchAccounts(userId) {
  const colRef = collection(db, "users", userId, "accounts");
  const snap = await getDocs(colRef);
  return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

// Create a new trading account
export async function createAccount(userId, accountName, type = "Futures", settingsOverrides = {}) {
  const colRef = collection(db, "users", userId, "accounts");
  const settings = fixNumericDefaults({
    ...defaultSettings,
    ...settingsOverrides,
    "Trade Mode": type,
  });
  const docRef = await addDoc(colRef, {
    name: accountName,
    type,
    created: serverTimestamp(),
    settings,
  });
  return docRef.id;
}

export async function getNextTradeNumber() {
  // Use current timestamp in milliseconds as integer trade number
  return Date.now();
}



async function deleteCollection(colRef, batchSize = 250) {
  while (true) {
    const snap = await getDocs(query(colRef, limit(batchSize)));
    if (snap.empty) break;

    const batch = writeBatch(db);
    snap.docs.forEach(d => batch.delete(d.ref));
    await batch.commit();
  }
}

/**
 * Deletes an account and its known subcollections so nothing is left behind.
 * Add/remove subcollection names here if you introduce more.
 */
export async function deleteAccount(userId, accountId) {
  if (!userId || !accountId) return;

  const accountRef = doc(db, "users", userId, "accounts", accountId);

  // List the subcollections you write to under an account:
  // - "journal"    -> where trades are appended
  // - "lessons"    -> where lessons are stored
  // - "settings"   -> if you store settings as a subcollection
  // Add additional names if your schema includes more subcollections.
  const SUBCOLLECTIONS = ["journal", "settings", "daily_insights", "connections", "ai_feedback"];
  // Delete each subcollection (if it exists)
  for (const name of SUBCOLLECTIONS) {
    const subColRef = collection(accountRef, name);
    await deleteCollection(subColRef); // safe even if empty
  }

  // Finally delete the account document itself
  await deleteDoc(accountRef);
}

export async function fetchLastNJournalRows(userId, accountId, n = 3) {
  const journalRef = collection(db, "users", userId, "accounts", accountId, "journal");
  // If you store a trade number or timestamp, order by that. If you store date as ISO string, you can order by "date"
  const q = query(journalRef, orderBy("tradeNum", "desc"), limit(n));
  const snap = await getDocs(q);
  // Convert docs to plain objects, reverse to get oldest-to-newest order
  return snap.docs.map(doc => doc.data()).reverse();
}


// userId = user's UID, accountId = selected account (if you have account grouping)
export async function clearJournalRows(userId, accountId) {
  const colRef = collection(db, "users", userId, "accounts", accountId, "journal");
  const snap = await getDocs(colRef);
  const batch = [];
  snap.forEach((docSnap) => {
    batch.push(deleteDoc(docSnap.ref));
  });
  // Await all deletions in parallel
  await Promise.all(batch);
}

export async function getAllAccountsForUser(userId) {
  const q = collection(db, "users", userId, "accounts");
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}


// USER SETTINGS
export async function getUserSettings(userId) {
  // Use your prefs doc, as before
  const docRef = doc(db, "users", userId, "settings", "prefs");
  const snap = await getDoc(docRef);
  return snap.exists() ? snap.data() : {};
}

export async function updateUserSettings(userId, newPrefs) {
  const docRef = doc(db, "users", userId, "settings", "prefs");
  await setDoc(docRef, newPrefs, { merge: true });
}

// ACCOUNT SETTINGS
export async function getAccountSettings(userId, accountId) {
  const docRef = doc(db, "users", userId, "accounts", accountId);
  const snap = await getDoc(docRef);
  return snap.exists() ? snap.data() : {};
}

export async function updateAccountSettings(userId, accountId, settingsObj) {
  const docRef = doc(db, "users", userId, "accounts", accountId);
  await updateDoc(docRef, settingsObj); // Only update provided fields
}




// Fetch all lessons for the current user (latest first)
const userLessonsRef = () => {
  const user = getAuth().currentUser;
  if (!user) throw new Error("Not logged in");
  return collection(db, "users", user.uid, "lessons");
};

export async function fetchLessonsRows() {
  const q = query(userLessonsRef(), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function appendLesson(lessonText) {
  return addDoc(userLessonsRef(), {
    lesson: lessonText,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateLesson(id, lessonText) {
  const user = getAuth().currentUser;
  if (!user) throw new Error("Not logged in");
  const ref = doc(db, "users", user.uid, "lessons", id);
  return updateDoc(ref, {
    lesson: lessonText,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteLesson(id) {
  const user = getAuth().currentUser;
  if (!user) throw new Error("Not logged in");
  const ref = doc(db, "users", user.uid, "lessons", id);
  return deleteDoc(ref);
}


// Utility: Removes all undefined fields from an object
export function removeUndefined(obj) {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined));
}

function subsCol(userId) {
  return collection(db, "users", userId, "subscribers");
}



function parseYMD(yyyyMMdd) {
  if (!yyyyMMdd || typeof yyyyMMdd !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(yyyyMMdd);
  if (!m) return null;
  return new Date(+m[1], +m[2] - 1, +m[3], 0, 0, 0, 0);
}

/** Normalize raw email for equality checks */
function normalizeEmail(raw) {
  return (raw || "").trim().toLowerCase();
}

/** For Gmail/Googlemail, remove dots and +tags so variants match */
function canonicalizeGmail(lower) {
  const m = lower.match(/^([^@]+)@(gmail\.com|googlemail\.com)$/);
  if (!m) return lower;
  const local = m[1].replace(/\./g, "").replace(/\+.*/, "");
  return `${local}@gmail.com`;
}



/** Inclusive in-range check */
function isTodayWithin(start, end) {
  const s = toDateAny(start);
  const e = toDateAny(end);
  if (!s || !e) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today >= s && today <= e;
}




//const db = getFirestore();
const topSubs = () => collection(db, "subscribers");
const normEmail = (s) => (s || "").trim().toLowerCase();

const norm = (s) => (s || "").trim().toLowerCase();
const canonGmail = (lower) => {
  const m = lower.match(/^([^@]+)@(gmail\.com|googlemail\.com)$/);
  if (!m) return lower;
  const local = m[1].replace(/\./g, "").replace(/\+.*/, "");
  return `${local}@gmail.com`;
};

function toDateAny(input) {
  if (!input) return null;
  if (typeof input === "object" && typeof input.seconds === "number") {
    const d = new Date(input.seconds * 1000); d.setHours(0,0,0,0); return d;
  }
  if (typeof input === "string") {
    const s = input.trim();
    let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (m) { const d = new Date(+m[1], +m[2]-1, +m[3]); d.setHours(0,0,0,0); return d; }
    m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
    if (m) { const d = new Date(+m[3], +m[1]-1, +m[2]); d.setHours(0,0,0,0); return d; }
    const d = new Date(s); if (!isNaN(d)) { d.setHours(0,0,0,0); return d; }
    return null;
  }
  if (input instanceof Date) { const d = new Date(+input); d.setHours(0,0,0,0); return d; }
  return null;
}
const today = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };
const inRangeToday = (s,e) => { const S=toDateAny(s), E=toDateAny(e), T=today(); return !!(S&&E&&T>=S&&T<=E); };

function dbg(...args) { console.debug("[SUBCHECK]", ...args); }
function redact(rec) {
  // Only show fields relevant to the gate (hide anything else)
  return {
    id: rec.__id || null,
    ownerUid: rec.ownerUid || null,
    email: rec.email || null,
    isAdmin: !!rec.isAdmin,
    startDate: rec.startDate ?? null,
    endDate: rec.endDate ?? null,
  };
}


const todayMidnight = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };


/*// ---- Admin page (owner-scoped) ----
export async function fetchSubscribers(userId) {
  if (!userId) return [];
  const colRef = collection(db, `users/${userId}/subscribers`);
  const snap = await getDocs(query(colRef));
  const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  return Array.isArray(rows) ? rows : [];
}
*/

export async function fetchSubscribers(_userId) {
  const q = query(collection(db, "subscribers"), orderBy("email"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/**
 * Read a single subscriber by UID (owner view).
 */
export async function fetchSubscriberById(userId) {
  if (!userId) return null;
  const ref = doc(db, "subscribers", userId);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Create/Upsert the subscriber at doc id = userId.
 * Writes both snake_case and camelCase audit fields for compatibility.
 */
export async function createSubscriber(userId, data) {
  if (!userId) throw new Error("createSubscriber: `userId` is required for one-to-one top-level schema.");
  const ref = doc(db, "subscribers", userId);
  const now = serverTimestamp();
  const payload = {
    ...data,
    userId,
    created_at: now,
    updated_at: now,
    createdAt: now,
    updatedAt: now,
  };
  await setDoc(ref, payload, { merge: true });
  return userId;
}

/**
 * Update an existing subscriber by id.
 * Keeps both underscore and camelCase `updated_` fields in sync.
 */
export async function updateSubscriber(_userId, id, data) {
  if (!id) throw new Error("updateSubscriber: `id` is required.");
  const ref = doc(db, "subscribers", id);
  const now = serverTimestamp();
  const payload = {
    ...data,
    updated_at: now,
    updatedAt: now,
  };
  await updateDoc(ref, payload);
}

/**
 * Delete subscriber by id.
 */
export async function deleteSubscriber(_userId, id) {
  if (!id) throw new Error("deleteSubscriber: `id` is required.");
  await deleteDoc(doc(db, "subscribers", id));
}

/**
 * Check if a referral code already exists across ALL subscribers (global uniqueness).
 * `userId` param preserved for backward-compat calls but not used.
 */
export async function referralCodeExists(_userId, code) {
  if (!code) return false;
  const q = query(
    collection(db, "subscribers"),
    where("referralCode", "==", String(code).toUpperCase()),
    limit(1)
  );
  const snap = await getDocs(q);
  return !snap.empty;
}


/**
 * Probe all 'subscribers' subcollections across the project for a given email.
 * Returns first few matches with their full document path so you know where they live.
 */
export async function probeSubscribersByEmail(email, max = 10) {
  if (!email) return [];
  const q = query(
    collectionGroup(db, "subscribers"),
    where("email", "==", String(email).toLowerCase()),
    limit(max)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({
    id: d.id,
    path: d.ref.path,
    ...d.data()
  }));
}


/**
 * Optional: log which project the app is connected to (helps find env mixups)
 */
export async function logFirestoreEnv() {
  try {
    const { getApp } = await import("firebase/app");
    // eslint-disable-next-line no-console
    console.log("[Firestore] projectId:", getApp().options?.projectId);
  } catch (e) {
    console.warn("[Firestore] Unable to read projectId", e);
  }
}


export async function probeSubscribersByUserId(userId, ownerField = "userId", max = 10) {
  if (!userId) return [];
  const q = query(
    collectionGroup(db, "subscribers"),
    where(ownerField, "==", userId),
    limit(max)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({
    id: d.id,
    path: d.ref.path,
    ...d.data()
  }));
}



export async function checkActiveSubscriptionByEmail(email) {
  // 1. ADDED hasTrapoundU to the initial state
  const out = { allowed: false, active: false, admin: false, any: false, latestEndDate: null, isDemo: false, hasTrapoundU: false };
  const __debug = { emailInput: email, emailVariants: [], attempted: [], found: [], errors: [] };

  try {
    const lower = norm(email);
    const variants = Array.from(new Set([lower, canonGmail(lower)]))
      .filter(Boolean);
    __debug.emailVariants = variants;
    dbg("begin", { emailInput: email, variants });

    // ---------- 1) TOP-LEVEL /subscribers ----------
    for (const v of variants) {
      const q = query(topSubs(), where("email", "==", v));
      __debug.attempted.push({ path: "subscribers", filter: { email: v } });
      dbg("query top-level /subscribers by email:", v);
      try {
        const snap = await getDocs(q);
        dbg("top-level snapshot size:", snap.size);
        if (snap.size > 0) out.any = true;

        snap.forEach(d => {
          const rec = { __id: d.id, ...(d.data() || {}) };
          __debug.found.push({ path: "subscribers", ...redact(rec) });

          if (rec.isAdmin) out.admin = true;
          if (rec.isDemo) out.isDemo = true;
          
          // 2. ADDED Trapound U check
          if (rec.hasTrapoundU) out.hasTrapoundU = true; 

          if (inRangeToday(rec.startDate, rec.endDate)) out.active = true;
          const end = toDateAny(rec.endDate);
          if (end && (!out.latestEndDate || end > out.latestEndDate)) out.latestEndDate = end;
        });
      } catch (e) {
        dbg("top-level query error:", e.code || e.name, e.message);
        __debug.errors.push({ path: "subscribers", email: v, code: e.code || e.name, message: e.message });
        if (e.code === "permission-denied") {
          out.__permDenied = true;
          // Continue to legacy attempt; do not throw
        }
      }
    }

    // ---------- 2) LEGACY fallback: /users/*/subscribers (collectionGroup) ----------
    if (!out.any) {
      for (const v of variants) {
        const cg = collectionGroup(db, "subscribers");
        const q = query(cg, where("email", "==", v));
        __debug.attempted.push({ path: "users/*/subscribers (collectionGroup)", filter: { email: v } });
        dbg("query legacy collectionGroup by email:", v);
        try {
          const snap = await getDocs(q);
          dbg("legacy snapshot size:", snap.size);
          if (snap.size > 0) out.any = true;

          snap.forEach(d => {
            const rec = { __id: d.id, ...(d.data() || {}) };
            __debug.found.push({ path: "users/*/subscribers", ...redact(rec) });

            if (rec.isAdmin) out.admin = true;
            
            // 3. ADDED Trapound U check
            if (rec.hasTrapoundU) out.hasTrapoundU = true;

            if (inRangeToday(rec.startDate, rec.endDate)) out.active = true;
            const end = toDateAny(rec.endDate);
            if (end && (!out.latestEndDate || end > out.latestEndDate)) out.latestEndDate = end;
          });
        } catch (e) {
          dbg("legacy query error:", e.code || e.name, e.message);
          __debug.errors.push({ path: "users/*/subscribers", email: v, code: e.code || e.name, message: e.message });
        }
      }
    }

    out.allowed = out.active || out.admin;
    out.__debug = __debug;
    dbg("result", out);
    return out;

  } catch (e) {
    console.error("checkActiveSubscriptionByEmail failed (outer):", e);
    out.__debug = { ...__debug, outerError: { code: e.code || e.name, message: e.message } };
    return out; // keep false/blocked on error
  }
}

function todayLocalISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// SAVE at user level: users/{userId}/pretrade_checklists/{YYYY-MM-DD}
export async function savePreTradeChecklist(userId, _accountId /* keep for API compat */, selections, date = todayLocalISO()) {
  const ref = doc(db, "users", userId, "pretrade_checklists", date);
  await setDoc(ref, { ...selections, date, savedAt: serverTimestamp() }, { merge: true });
}

// READ user level first; fallback to old account level if it exists
export async function getTodayPreTradeChecklist(userId, accountId /* optional */, date = todayLocalISO()) {
  // 1) New (user-level)
  const uRef = doc(db, "users", userId, "pretrade_checklists", date);
  const uSnap = await getDoc(uRef);
  if (uSnap.exists()) return uSnap.data();

  // 2) Legacy (account-level) fallback
  if (accountId) {
    const aRef = doc(db, "users", userId, "accounts", accountId, "pretrade_checklists", date);
    const aSnap = await getDoc(aRef);
    if (aSnap.exists()) return aSnap.data();
  }
  return null;
}

// "Shown today" stamp should be user-scoped
const PTC_SHOWN_KEY = "PretradeChecklistShownDate";
export async function setChecklistShown(userId, _accountId /* compat */, dateStr = todayLocalISO()) {
  const userRef = doc(db, "users", userId);
  await setDoc(userRef, { [PTC_SHOWN_KEY]: dateStr }, { merge: true });
  return dateStr;
}



export async function getUserWelcomeStatus(userId) {
  if (!userId) return true; 

  try {
    const checkTrue = (val) => val === true || String(val).trim().toLowerCase() === "true";
    let hasSeen = false;

    // 1. Check the prefs document FIRST (where we are now saving it)
    const prefsRef = doc(db, "users", userId, "settings", "prefs");
    const prefsSnap = await getDocFromServer(prefsRef);
    if (prefsSnap.exists() && checkTrue(prefsSnap.data()?.hasSeenWelcomeVideo)) {
      hasSeen = true;
    }

    // 2. Fallback to root user doc (just in case older users have it there)
    if (!hasSeen) {
      const userRef = doc(db, "users", userId);
      const userSnap = await getDocFromServer(userRef);
      if (userSnap.exists() && checkTrue(userSnap.data()?.hasSeenWelcomeVideo)) {
        hasSeen = true;
      }
    }

    return hasSeen; 
  } catch (error) {
    console.error("Error checking welcome status:", error);
    return true; 
  }
}

export async function markWelcomeVideoAsSeen(userId) {
  if (!userId) return;

  try {
    // Write to settings/prefs instead of the restricted root user doc
    const prefsRef = doc(db, "users", userId, "settings", "prefs");
    
    await setDoc(prefsRef, {
      hasSeenWelcomeVideo: true
    }, { merge: true });
    
    console.log("Welcome video status saved successfully to prefs.");
  } catch (error) {
    console.error("Error updating welcome status:", error);
  }
}



// --- Helper logic for the migration ---
const FUTURES_TICK_SIZES = {
  YM: 1, MYM: 1, ES: 0.25, MES: 0.25, NQ: 0.25, MNQ: 0.25,
  RTY: 0.1, M2K: 0.1, GC: 0.1, MGC: 0.1, CL: 0.01, MCL: 0.01,
  NG: 0.001, MNG: 0.001, SI: 0.005, SIL: 0.005, BTC: 5, MBT: 5,
};

function getMigrateFuturesTick(productStr) {
  if (!productStr) return 0.25;
  const sym = String(productStr).split(" - ")[0].split("·")[0].trim().toUpperCase();
  return FUTURES_TICK_SIZES[sym] || 0.25;
}

function getMigrateForexPip(symbol) {
  if (!symbol) return 0.0001;
  const s = String(symbol).toUpperCase();
  const FOREX_INDICES = ["US30", "NAS100", "SPX500", "GER30", "UK100", "GER40", "US2000", "JP225", "HK50", "AUS200"];
  if (FOREX_INDICES.some(idx => s.includes(idx))) return 1; 
  if (s.includes("JPY") || s.includes("XAU") || s.includes("GOLD") || s.includes("XAG") || s.includes("SILVER")) return 0.01;
  return 0.0001;
}

// --- The GLOBAL Migration Script (All Users, All Accounts) ---
export const migrateALLHistoricalTradesGlobal = async () => {
  console.log("Starting GLOBAL trade migration for ALL users and ALL accounts...");
  
  try {
    // 1. Get ALL users in the entire database
    const usersRef = collection(db, "users");
    const usersSnap = await getDocs(usersRef);
    
    let totalUpdated = 0;
    let totalSkipped = 0;
    let usersProcessed = 0;

    // 2. Loop through every single user
    for (const userDoc of usersSnap.docs) {
      const userId = userDoc.id;
      usersProcessed++;
      console.log(`Processing user ${usersProcessed} of ${usersSnap.size}: ${userId}`);

      // 3. Get all accounts for THIS user
      const accountsRef = collection(db, "users", userId, "accounts");
      const accountsSnap = await getDocs(accountsRef);
      
      // 4. Loop through every account
      for (const accountDoc of accountsSnap.docs) {
        const accountId = accountDoc.id;
        
        // 5. Get all journal entries (trades) for this specific account
        const journalRef = collection(db, "users", userId, "accounts", accountId, "journal");
        const journalSnap = await getDocs(journalRef);
        
        // 6. Loop through the trades
        for (const tradeDoc of journalSnap.docs) {
          const tradeData = tradeDoc.data();
          
          // Skip if already migrated
          if (tradeData.tickSize !== undefined && tradeData.assetClass !== undefined) {
             totalSkipped++;
             continue;
          }

          // Skip if it's a deposit/withdrawal row instead of a real trade
          if (tradeData["Funding Type"] || tradeData.isFunding) {
             continue;
          }

          let aClass = String(tradeData["Account Type"] || tradeData.AssetClass || "").toUpperCase();
          const product = String(tradeData.Product || "").toUpperCase();

          if (!aClass || aClass === "UNDEFINED") {
             if (product.includes("EUR") || product.includes("USD") || product.includes("JPY") || product.includes("GBP")) {
                 aClass = "FOREX";
             } else if (FUTURES_TICK_SIZES[product.split(" - ")[0]]) {
                 aClass = "FUTURES";
             } else if (tradeData["Strike"] || tradeData["Option Type"]) {
                 aClass = "OPTIONS";
             } else {
                 aClass = "STOCKS"; 
             }
          }

          let tSize = 0.01; // Default for Stocks & Options
          if (aClass === "FUTURES") {
              tSize = getMigrateFuturesTick(product);
          } else if (aClass === "FOREX") {
              tSize = getMigrateForexPip(product);
          }

          // Write the update directly to the document reference
          await updateDoc(tradeDoc.ref, {
             tickSize: tSize,
             assetClass: aClass
          });
          
          totalUpdated++;
        }
      }
    }
    
    console.log(`GLOBAL Migration Complete! Users: ${usersProcessed}. Updated: ${totalUpdated}. Skipped: ${totalSkipped}.`);
    alert(`GLOBAL Migration Complete!\n\nUsers Processed: ${usersProcessed}\nTrades Updated: ${totalUpdated}\nTrades Skipped: ${totalSkipped} (already migrated)`);
    
  } catch (error) {
    console.error("Global Migration failed: ", error);
    alert("Global Migration failed. Check console for details.");
  }
};

export const getUsersWithTodayInsight = async () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  const todayStr = `${year}-${month}-${day}`;

  try {
    const insightsQuery = query(
      collectionGroup(db, "daily_insights"),
      where("date", "==", todayStr)
    );

    const snapshot = await getDocs(insightsQuery);
    const results = [];

    // Use Promise.all to fetch user documents concurrently
    const fetchPromises = snapshot.docs.map(async (docSnap) => {
      if (docSnap.id === "current_insight") {
        const pathSegments = docSnap.ref.path.split("/");
        const userId = pathSegments[1];
        const accountId = pathSegments[3];

        let email = "Unknown Email";
        
        try {
          // Fetch the specific user's document to get their email
          const userSnap = await getDoc(doc(db, "users", userId));
          if (userSnap.exists()) {
            // Check for email in root or nested settings
            const userData = userSnap.data();
            email = userData.email || userData.settings?.email || "Email not found on user doc";
          }
        } catch (e) {
          console.warn(`Could not fetch user document for ${userId}`);
        }

        results.push({
          email: email, // <-- Email added here
          userId: userId,
          accountId: accountId,
          insight: docSnap.data(),
        });
      }
    });

    // Wait for all the individual user documents to be fetched
    await Promise.all(fetchPromises);

    console.log(`Found ${results.length} insights generated today:`, results);
    return results;
  } catch (error) {
    console.error("Error querying daily insights:", error);
    return [];
  }
};
